import * as React from 'react';
import styled from '@emotion/styled';
import { MdxLink } from '../pages/shared/MdxLink';
import { cardTitleTypography } from './typography';

export interface CardWidgetProps {
  title: string;
  description?: React.ReactNode;
  metadata?: React.ReactNode;
  cover?: { src: string; fit?: 'cover' | 'contain' };
  icon?: React.ReactNode;
  compact?: boolean;
  featured?: boolean;
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  action?: string;
  descriptionLines?: number;
  expandable?: boolean;
  id?: string;
  className?: string;
}

export const CardWidget: React.FC<CardWidgetProps> = ({
  title,
  description,
  metadata,
  cover,
  icon,
  compact = false,
  featured = false,
  href,
  target,
  action,
  descriptionLines,
  expandable = false,
  id,
  className
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const [failedCover, setFailedCover] = React.useState<string | null>(null);
  const descriptionId = React.useId();
  const content = (
    <>
      {cover && cover.src !== failedCover && (
        <S.Cover
          src={cover.src}
          alt=""
          loading="lazy"
          $fit={cover.fit ?? 'cover'}
          onError={() => setFailedCover(cover.src)}
        />
      )}
      <S.Body $compact={compact}>
        {icon && <S.Icon>{icon}</S.Icon>}
        <S.Content>
          {metadata && <S.Metadata>{metadata}</S.Metadata>}
          <S.Title as={compact ? 'div' : 'h3'}>{title}</S.Title>
          {description && (
            <S.Description id={descriptionId} $lines={expanded ? undefined : descriptionLines}>
              {description}
            </S.Description>
          )}
          {!href && expandable && descriptionLines && (
            <S.Expand
              type="button"
              aria-expanded={expanded}
              aria-controls={descriptionId}
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded ? 'Show less' : 'Read more'}
            </S.Expand>
          )}
          {href && action && (
            <S.Action $featured={featured}>
              {action} <span aria-hidden="true">→</span>
            </S.Action>
          )}
        </S.Content>
      </S.Body>
    </>
  );
  return href ? (
    <S.Link
      $featured={featured}
      id={id}
      className={className}
      href={href}
      target={target}
      rel={target === '_blank' ? 'noreferrer' : undefined}
    >
      {content}
    </S.Link>
  ) : (
    <S.Card id={id} className={className} $featured={featured}>
      {content}
    </S.Card>
  );
};

namespace S {
  export const Card = styled.article<{ $featured?: boolean }>`
    min-width: 0;
    box-sizing: border-box;
    overflow: hidden;
    background: ${(p) => p.theme.card.background};
    border: 1px solid ${(p) => p.theme.surface.border};
    border-radius: 20px;
    box-shadow: ${(p) => p.theme.card.shadow};
    background-image: ${(p) =>
      p.$featured
        ? `radial-gradient(ellipse at top right, color-mix(in srgb, ${p.theme.home.walkthroughButton.border} 18%, transparent), transparent 75%)`
        : 'none'};
    border-color: ${(p) => (p.$featured ? p.theme.home.walkthroughButton.border : p.theme.surface.border)};
  `;
  export const Link = styled(Card.withComponent(MdxLink))`
    display: block;
    color: ${(p) => p.theme.text.paragraph};
    text-decoration: none !important;
    transition:
      border-color 160ms ease,
      background-color 160ms ease;
    &:hover {
      border-color: ${(p) => (p.$featured ? p.theme.home.walkthroughButton.border : p.theme.text.special)};
      background-color: ${(p) =>
        p.$featured ? p.theme.home.walkthroughButton.hoverBackground : p.theme.card.hoverBackground};
      text-decoration: none;
    }
    &:focus-visible {
      outline: 2px solid ${(p) => (p.$featured ? p.theme.home.walkthroughButton.focus : p.theme.text.special)};
      outline-offset: 3px;
    }
    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `;
  export const Body = styled.div<{ $compact: boolean }>`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: ${(p) => (p.$compact ? '14px' : '24px')};
  `;
  export const Content = styled.div`
    min-width: 0;
    flex: 1;
  `;
  export const Metadata = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px 12px;
    margin-bottom: 16px;
  `;
  export const Title = styled.h3`
    && {
      ${(p) => cardTitleTypography(p.theme)};
      margin: 0;
      overflow-wrap: anywhere;
    }
  `;
  export const Description = styled.div<{ $lines?: number }>`
    color: ${(p) => p.theme.text.paragraph};
    font-size: 14px;
    line-height: 1.6;
    margin-top: 12px;
    white-space: pre-line;
    overflow-wrap: anywhere;
    ${(p) =>
      p.$lines &&
      `display: -webkit-box; -webkit-line-clamp: ${p.$lines}; -webkit-box-orient: vertical; overflow: hidden;`}
  `;
  export const Action = styled.div<{ $featured: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 12px;
    color: ${(p) => (p.$featured ? p.theme.text.heading : p.theme.text.special)};
    background: ${(p) => (p.$featured ? p.theme.home.walkthroughButton.background : 'transparent')};
    padding: ${(p) => (p.$featured ? '10px 16px' : '0')};
    border-radius: 10px;
    border: ${(p) => (p.$featured ? `1px solid ${p.theme.home.walkthroughButton.border}` : 'none')};
    font-size: 14px;
    font-weight: 600;
    margin-top: 20px;
  `;
  export const Expand = styled.button`
    color: ${(p) => p.theme.text.special};
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    font-size: 13px;
    cursor: pointer;
    margin-top: 16px;
    &:hover {
      text-decoration: underline;
    }
    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 4px;
    }
  `;
  export const Icon = styled.div`
    flex: 0 0 64px;
    display: grid;
    place-items: center;
    aspect-ratio: 1;
    border-radius: 12px;
    background: ${(p) => p.theme.docsCallout.info.pillBackground};
    color: ${(p) => p.theme.docsCallout.info.pillText};
    font-size: 30px;
  `;
  export const Cover = styled.img<{ $fit: 'cover' | 'contain' }>`
    && {
      display: block;
      width: 100%;
      max-width: 100%;
      height: ${(p) => (p.$fit === 'cover' ? '200px' : 'auto')};
      max-height: ${(p) => (p.$fit === 'contain' ? '230px' : 'none')};
      object-fit: ${(p) => p.$fit};
      margin: 0;
      border: 0;
      border-radius: 0;
      box-shadow: none;
      transform: none;
      transition: none;
      background: ${(p) => p.theme.hero.background};
    }
  `;
}
